import { useMemo, useState, useRef, useCallback, type DragEvent } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Inbox,
  LayoutList,
  CalendarDays,
  GripVertical,
  PartyPopper,
  CircleDashed,
  Zap,
  ShieldAlert,
  Sparkles,
  Archive,
  ListTree,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ArrowUpDown,
  Filter,
  Sun,
  Target,
  Clock,
  AlertTriangle,
  Plus,
  Timer,
  ListChecks,
  LayoutGrid,
  Columns,
} from 'lucide-react';
import type { Task, GroupingState, SortingState, Priority } from '@/types';
import { normalizeStatus, TASK_STATUS, isCompletedStatus } from '@/constants/taskStatuses';
import { useMyDay, type MyDaySection } from '@/hooks/useMyDay';
import { useCalendar, type CalendarViewMode } from '@/hooks/useCalendar';
import { getOverdueTasks } from '@/utils/taskUtils';
import {
  groupTasksByMultipleLevels,
  flattenGroupedItems,
  getTaskGroupingOptions,
  createDefaultGroupingState,
  toggleGroupCollapse,
  getTaskSortingOptions,
  sortItems,
  createDefaultSortingState,
} from '@/utils/grouping';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import {
  ViewToolbar,
  ShowCompletedToggle,
  MultiGroupingSelector,
  MultiSortingSelector,
  MultiFilterSelector,
} from '@/components/shared/view-toolbar';
import {
  StatusSelect,
  PrioritySelect,
  InlineDatePicker,
  AssigneesEditor,
  TagsEditor,
} from '@/components/shared/inline-editors';
import { CustomFieldsInline } from '@/components/fields/CustomFieldsInline';
import { TimeGrid } from '@/components/calendar';
import clsx from 'clsx';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  isToday,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { useViewData } from '@/hooks/useViewData';
import { useAppStore } from '@/store/appStore';
import { useTaskActions } from '@/hooks/useTaskActions';
import { handleError } from '@/utils/errorHandler';

type InboxPanel = 'today' | 'list' | 'calendar';

export function InboxView() {
  const {
    tasks,
    filters,
    grouping,
    sorting,
    availableAssignees,
    availableTags,
    onFilterChange,
    onGroupingChange,
    onSortingChange,
    onTaskClick,
  } = useViewData();
  const setMobileSidebarOpen = useAppStore((state) => state.setMobileSidebarOpen);
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const onOpenSidebar = () => setMobileSidebarOpen(true);
  const { handleUpdateTask: onTaskUpdate, handleCreateTask } = useTaskActions();
  const { user } = useAuth();

  // Multi-toggle panels (independently activatable)
  const [activePanels, setActivePanels] = useState<Set<InboxPanel>>(() => new Set(['today']));

  const togglePanel = useCallback((panel: InboxPanel) => {
    setActivePanels(prev => {
      const next = new Set(prev);
      if (next.has(panel)) {
        // Don't allow removing the last panel
        if (next.size <= 1) return prev;
        next.delete(panel);
      } else {
        next.add(panel);
      }
      return next;
    });
  }, []);

  // Always call useMyDay (hook rules)
  const myDay = useMyDay();

  // Calendar hook uses all tasks (will be given visibleTasks after filtering below)
  // We call with `tasks` here; we'll filter in the render. useCalendar needs stable call.
  const calendarHook = useCalendar({ tasks, initialViewMode: 'week' });

  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<Date | null>(null);

  const [localGrouping, setLocalGrouping] = useState<GroupingState>(
    () => grouping || createDefaultGroupingState()
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(
    () => sorting || createDefaultSortingState()
  );

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const nextWeek = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return d;
  }, [today]);

  // Inbox shows only tasks assigned to the current user
  const visibleTasks = useMemo(() => {
    if (!user?.id) return [];
    return tasks.filter(t => t.assignees?.includes(user.id));
  }, [tasks, user?.id]);

  // Sort tasks using multi-level sorting
  const sortedTasks = useMemo(() => {
    return sortItems(visibleTasks, localSorting, (a, b, field, order) => {
      let comparison = 0;

      switch (field) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status': {
          const statusOrder: Record<string, number> = {
            [TASK_STATUS.IN_PROGRESS]: 1,
            [TASK_STATUS.OPEN]: 2,
            [TASK_STATUS.TODO]: 3,
            [TASK_STATUS.BLOCKED]: 4,
            [TASK_STATUS.COMPLETED]: 5,
            [TASK_STATUS.CANCELLED]: 6,
          };
          const statusA = statusOrder[normalizeStatus(a.status)] ?? 99;
          const statusB = statusOrder[normalizeStatus(b.status)] ?? 99;
          comparison = statusA - statusB;
          break;
        }
        case 'dueDate': {
          const dateA = typeof a.dueDate === 'number' ? a.dueDate : (a.dueDate ?? Infinity);
          const dateB = typeof b.dueDate === 'number' ? b.dueDate : (b.dueDate ?? Infinity);
          comparison = dateA - dateB;
          break;
        }
        case 'dateCreated': {
          const createdA = a.dateCreated ?? 0;
          const createdB = b.dateCreated ?? 0;
          comparison = createdA - createdB;
          break;
        }
        case 'priority': {
          const priorityOrder: Record<string, number> = { urgent: 1, high: 2, normal: 3, low: 4 };
          const priorityA = priorityOrder[a.priority || 'normal'] ?? 99;
          const priorityB = priorityOrder[b.priority || 'normal'] ?? 99;
          comparison = priorityA - priorityB;
          break;
        }
        case 'assignee': {
          const assigneeA = (a.assignees || [])[0] || '';
          const assigneeB = (b.assignees || [])[0] || '';
          comparison = assigneeA.localeCompare(assigneeB);
          break;
        }
        default:
          comparison = 0;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [visibleTasks, localSorting]);

  // Group tasks using the new grouping system
  const groupedTasks = useMemo(() => {
    return groupTasksByMultipleLevels(sortedTasks, localGrouping);
  }, [sortedTasks, localGrouping]);

  // Tasks grouped by date for calendar
  const tasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();

    visibleTasks.forEach((task) => {
      if (task.dueDate) {
        const dueDate = typeof task.dueDate === 'number' ? new Date(task.dueDate) : task.dueDate;
        const dateKey = format(dueDate, 'yyyy-MM-dd');
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, task]);
      }
    });

    return map;
  }, [visibleTasks]);

  const stats = useMemo(() => {
    return {
      total: visibleTasks.length,
      overdue: getOverdueTasks(visibleTasks).length,
      today: visibleTasks.filter((t) => {
        if (!t.dueDate) return false;
        const due = typeof t.dueDate === 'number' ? new Date(t.dueDate) : t.dueDate;
        due.setHours(0, 0, 0, 0);
        return due.getTime() === today.getTime();
      }).length,
      upcoming: visibleTasks.filter((t) => {
        if (!t.dueDate) return false;
        const due = typeof t.dueDate === 'number' ? new Date(t.dueDate) : t.dueDate;
        return due >= today && due < nextWeek;
      }).length,
    };
  }, [visibleTasks, today, nextWeek]);

  // Unique values for filters
  const uniqueValues = useMemo(() => {
    return {
      statuses: Array.from(new Set(visibleTasks.map((t) => normalizeStatus(t.status)))).filter(
        Boolean
      ),
      assignees: Array.from(new Set(visibleTasks.flatMap((t) => (t.assignees || []).filter(Boolean)))).filter((a): a is string => a !== null),
      tags: Array.from(new Set(visibleTasks.flatMap((t) => (t.tags || []).filter(Boolean)))).filter((t): t is string => t !== null),
      priorities: Array.from(new Set(visibleTasks.map((t) => t.priority).filter(Boolean))),
    };
  }, [visibleTasks]);

  const handleTaskClick = (task: Task) => {
    onTaskClick?.(task);
  };

  // Flatten grouped tasks for virtualization
  const flattenedItems = useMemo(() => {
    return flattenGroupedItems(groupedTasks, localGrouping, localGrouping.primary !== 'none');
  }, [groupedTasks, localGrouping]);

  const taskListRef = useRef<HTMLDivElement>(null);

  // Heights for virtualization
  const TASK_CARD_ESTIMATE = 120; // Estimate for initial sizing

  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => taskListRef.current,
    getItemKey: (index) => flattenedItems[index]?.key ?? index,
    estimateSize: (index) => {
      const item = flattenedItems[index];
      return item.type === 'header' ? 36 : TASK_CARD_ESTIMATE;
    },
    overscan: 5,
    measureElement: (element) => {
      return element.getBoundingClientRect().height;
    },
  });

  // TimeGrid days for calendar panel (week/day sub-views)
  const timeGridWeekDays = useMemo(() => {
    const ws = startOfWeek(calendarHook.currentDate, { weekStartsOn: 1 });
    const we = endOfWeek(calendarHook.currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: ws, end: we });
  }, [calendarHook.currentDate]);

  const timeGridDayDays = useMemo(() => [calendarHook.currentDate], [calendarHook.currentDate]);

  // Handler for TimeGrid task creation (includes scheduling)
  const handleTimeGridCreateTask = useCallback(
    async (taskData: Partial<Task>) => {
      try { await handleCreateTask(taskData); }
      catch (error) { handleError(error, { level: 'error', message: 'Errore nella creazione del task dal calendario' }); }
    },
    [handleCreateTask]
  );

  // Calendar label
  const calendarLabel = calendarHook.viewMode === 'month'
    ? calendarHook.monthLabel
    : calendarHook.viewMode === 'week'
      ? calendarHook.weekLabel
      : calendarHook.dayLabel;

  // Drag and drop handlers
  const handleDragStart = (e: DragEvent<HTMLDivElement>, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);

    // Add drag image
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDropTargetDate(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDate(date);
  };

  const handleDragLeave = () => {
    setDropTargetDate(null);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, date: Date) => {
    e.preventDefault();

    if (draggedTask && onTaskUpdate) {
      // Create a new date with the time from the original due date or default to 9 AM
      const newDueDate = new Date(date);
      if (draggedTask.dueDate) {
        const originalDue =
          typeof draggedTask.dueDate === 'number'
            ? new Date(draggedTask.dueDate)
            : draggedTask.dueDate;
        newDueDate.setHours(
          originalDue.getHours(),
          originalDue.getMinutes(),
          originalDue.getSeconds()
        );
      } else {
        newDueDate.setHours(9, 0, 0, 0);
      }

      onTaskUpdate(draggedTask.id, { dueDate: newDueDate.getTime() });
    }

    setDraggedTask(null);
    setDropTargetDate(null);
  };

  // Filter options with consistent ordering
  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
    );
  }, [uniqueValues, resolveDisplayName]);

  // Grouping handlers
  const handleGroupingChange = useCallback(
    (newGrouping: GroupingState) => {
      setLocalGrouping(newGrouping);
      onGroupingChange(newGrouping);
    },
    [onGroupingChange]
  );

  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      const newGrouping = toggleGroupCollapse(localGrouping, groupKey);
      handleGroupingChange(newGrouping);
    },
    [localGrouping, handleGroupingChange]
  );

  // Sorting handlers
  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setLocalSorting(newSorting);
      onSortingChange(newSorting);
    },
    [onSortingChange]
  );

  const getGroupIcon = (key: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      [TASK_STATUS.IN_PROGRESS]: <Zap className="w-3 h-3 text-accent-blue" />,
      [TASK_STATUS.OPEN]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
      [TASK_STATUS.TODO]: <CircleDashed className="w-3 h-3 text-text-quaternary" />,
      [TASK_STATUS.BLOCKED]: <ShieldAlert className="w-3 h-3 text-accent-rose" />,
      [TASK_STATUS.COMPLETED]: <Sparkles className="w-3 h-3 text-accent-emerald" />,
      [TASK_STATUS.CANCELLED]: <Archive className="w-3 h-3 text-accent-violet" />,
    };
    return iconMap[key] || <CircleDashed className="w-3 h-3 text-text-quaternary" />;
  };

  return (
    <div className="flex-1 flex overflow-hidden animate-fade-in">
      {/* Main Content */}
      <div
        className={clsx(
          'flex-1 flex flex-col overflow-hidden transition-all duration-300',
          selectedTaskId ? 'mr-0' : ''
        )}
      >
        {/* Header */}
        <ViewToolbar
          icon={<Inbox className="w-4 h-4 text-accent-amber" />}
          title="Inbox"
          subtitle={`${stats.total} task attivi`}
          onOpenSidebar={onOpenSidebar}
          leftContent={
            <div className="flex items-center flex-wrap min-w-0 gap-3">
              {/* Multi-toggle panel buttons */}
              <div className="flex items-center gap-0.5 p-0.5 bg-surface-tertiary/50 rounded-lg">
                {([
                  { key: 'today' as InboxPanel, icon: <Sun className="w-3.5 h-3.5" />, label: 'Oggi' },
                  { key: 'list' as InboxPanel, icon: <LayoutList className="w-3.5 h-3.5" />, label: 'Lista' },
                  { key: 'calendar' as InboxPanel, icon: <CalendarDays className="w-3.5 h-3.5" />, label: 'Calendario' },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => togglePanel(opt.key)}
                    className={clsx(
                      'flex items-center gap-1.5 rounded-md font-medium transition-all px-2.5 py-1.5 text-[11px]',
                      activePanels.has(opt.key)
                        ? 'bg-surface-primary text-text-primary shadow-sm'
                        : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-hover/50'
                    )}
                  >
                    {opt.icon}
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>

            </div>
          }
        />

        {/* Content Area - panels laid out side by side */}
        <div className="flex-1 flex overflow-hidden">
          {/* Today Panel */}
          {activePanels.has('today') && (
            <div className={clsx(
              'flex flex-col overflow-hidden',
              // Sizing: if alone, take all. If with others, limit width.
              activePanels.size === 1 ? 'flex-1' : 'w-[380px] shrink-0 border-r border-border-primary'
            )}>
              <TodayPanel
                myDay={myDay}
                onTaskClick={handleTaskClick}
                onTaskUpdate={onTaskUpdate}
                onCreateTask={handleCreateTask}
              />
            </div>
          )}

          {/* Task List Panel - Virtualized */}
          {activePanels.has('list') && (
            <div
              className={clsx(
                'flex flex-col',
                // If calendar is also active, limit width; otherwise flex
                activePanels.has('calendar') ? 'w-[380px] shrink-0 border-r border-border-primary' : 'flex-1'
              )}
            >
              {/* Sticky filter/grouping/sorting bar */}
              {filters && onFilterChange && (
                <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 border-b border-border-primary bg-surface-secondary/90 backdrop-blur-sm flex-wrap shrink-0">
                  <MultiFilterSelector
                    filters={{
                      status: filters.status || [],
                      assignees: filters.assignees || [],
                      tags: filters.tags || [],
                      priority: filters.priority || [],
                    }}
                    onFilterChange={onFilterChange}
                    availableOptions={filterOptions}
                    icon={<Filter className="w-3.5 h-3.5" />}
                    compact={activePanels.size > 1}
                  />
                  <MultiGroupingSelector
                    grouping={localGrouping}
                    onGroupingChange={handleGroupingChange}
                    availableOptions={getTaskGroupingOptions()}
                    icon={<ListTree className="w-3.5 h-3.5" />}
                    compact={activePanels.size > 1}
                  />
                  <MultiSortingSelector
                    sorting={localSorting}
                    onSortingChange={handleSortingChange}
                    availableOptions={getTaskSortingOptions()}
                    icon={<ArrowUpDown className="w-3.5 h-3.5" />}
                    compact={activePanels.size > 1}
                  />
                  <ShowCompletedToggle
                    showCompleted={filters.showCompleted}
                    onToggle={(show) => onFilterChange({ showCompleted: show })}
                    compact={activePanels.size > 1}
                  />
                </div>
              )}
              {visibleTasks.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-tertiary flex items-center justify-center">
                      <PartyPopper className="w-8 h-8 text-accent-emerald" />
                    </div>
                    <p className="text-[13px] font-medium text-text-secondary">Inbox vuota!</p>
                    <p className="text-[11px] text-text-quaternary mt-1">Nessun task da mostrare</p>
                  </div>
                </div>
              ) : (
                <div ref={taskListRef} className="flex-1 overflow-y-auto p-4">
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const item = flattenedItems[virtualRow.index];

                      if (item.type === 'header') {
                        const group = item.data as { key: string; label: string; items: Task[] };
                        return (
                          <div
                            key={item.key}
                            ref={rowVirtualizer.measureElement}
                            data-index={virtualRow.index}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            <button
                              onClick={() => handleToggleGroup(group.key)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 bg-surface-secondary/80 hover:bg-surface-secondary transition-colors"
                            >
                              <ChevronDown
                                className={clsx(
                                  'w-3 h-3 text-text-quaternary transition-transform',
                                  localGrouping.collapsedGroups.has(group.key) && '-rotate-90'
                                )}
                              />
                              {getGroupIcon(group.key)}
                              <span className="text-[11px] font-semibold text-text-secondary">
                                {group.label}
                              </span>
                              <span className="text-[10px] text-text-quaternary tabular-nums font-medium">
                                {group.items.length}
                              </span>
                              <div className="flex-1" />
                              <div className="h-px flex-1 max-w-[200px] bg-linear-to-r from-border-primary/50 to-transparent" />
                            </button>
                          </div>
                        );
                      }

                      const task = item.data as Task;
                      const taskIsOverdue =
                        typeof task.dueDate === 'number' ? task.dueDate < Date.now() : false;

                      return (
                        <div
                          key={item.key}
                          ref={rowVirtualizer.measureElement}
                          data-index={virtualRow.index}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <DraggableTaskCard
                            task={task}
                            onClick={() => handleTaskClick(task)}
                            isSelected={selectedTaskId === task.id}
                            isOverdue={taskIsOverdue}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            isDragging={draggedTask?.id === task.id}
                            onUpdate={onTaskUpdate}
                            availableAssignees={availableAssignees}
                            availableTags={availableTags}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Calendar Panel - Full calendar with Month/Week/Day + TimeGrid */}
          {activePanels.has('calendar') && (
            <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
              {/* Sticky calendar controls bar */}
              <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 border-b border-border-primary bg-surface-secondary/90 backdrop-blur-sm shrink-0">
                <div className="flex items-center gap-0.5 p-0.5 bg-surface-tertiary/50 rounded-lg">
                  {([
                    { mode: 'month' as CalendarViewMode, icon: <LayoutGrid className="w-3 h-3" />, label: 'Mese' },
                    { mode: 'week' as CalendarViewMode, icon: <Columns className="w-3 h-3" />, label: 'Sett.' },
                    { mode: 'day' as CalendarViewMode, icon: <Clock className="w-3 h-3" />, label: 'Giorno' },
                  ]).map((opt) => (
                    <button
                      key={opt.mode}
                      onClick={() => calendarHook.setViewMode(opt.mode)}
                      className={clsx(
                        'flex items-center gap-1 rounded-md font-medium transition-all px-2 py-1 text-[10px]',
                        calendarHook.viewMode === opt.mode
                          ? 'bg-surface-primary text-text-primary shadow-sm'
                          : 'text-text-tertiary hover:text-text-secondary'
                      )}
                    >
                      {opt.icon}
                      <span className="hidden sm:inline">{opt.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={calendarHook.goToToday}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-medium text-accent-violet bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
                >
                  Oggi
                </button>
                <div className="flex items-center gap-1 bg-surface-secondary rounded-lg border border-border-primary">
                  <button onClick={calendarHook.goToPrev} className="p-1 hover:bg-surface-hover rounded-l-lg transition-colors">
                    <ChevronLeft className="w-3.5 h-3.5 text-text-tertiary" />
                  </button>
                  <span className="px-2 py-0.5 text-[11px] font-medium text-text-primary min-w-[140px] text-center capitalize">
                    {calendarLabel}
                  </span>
                  <button onClick={calendarHook.goToNext} className="p-1 hover:bg-surface-hover rounded-r-lg transition-colors">
                    <ChevronRight className="w-3.5 h-3.5 text-text-tertiary" />
                  </button>
                </div>
              </div>
              {calendarHook.viewMode === 'month' && (
                <InboxMonthGrid
                  calendarHook={calendarHook}
                  visibleTasks={visibleTasks}
                  tasksByDate={tasksByDate}
                  selectedTaskId={selectedTaskId}
                  today={today}
                  draggedTask={draggedTask}
                  dropTargetDate={dropTargetDate}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onTaskClick={handleTaskClick}
                />
              )}
              {calendarHook.viewMode === 'week' && (
                <TimeGrid
                  tasks={visibleTasks}
                  days={timeGridWeekDays}
                  onUpdateTask={onTaskUpdate}
                  onCreateTask={handleTimeGridCreateTask}
                  onTaskClick={(task) => handleTaskClick(task)}
                />
              )}
              {calendarHook.viewMode === 'day' && (
                <TimeGrid
                  tasks={visibleTasks}
                  days={timeGridDayDays}
                  onUpdateTask={onTaskUpdate}
                  onCreateTask={handleTimeGridCreateTask}
                  onTaskClick={(task) => handleTaskClick(task)}
                />
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

interface DraggableTaskCardProps {
  task: Task;
  onClick: () => void;
  isSelected: boolean;
  isOverdue: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>, task: Task) => void;
  onDragEnd: () => void;
  isDragging: boolean;
  onUpdate?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  availableTags?: string[];
}

function DraggableTaskCard({
  task,
  onClick,
  isSelected,
  isOverdue,
  onDragStart,
  onDragEnd,
  isDragging,
  onUpdate,
  availableAssignees = [],
  availableTags = [],
}: DraggableTaskCardProps) {
  const canEdit = !!onUpdate;

  const handleUpdate = (updates: Partial<Task>) => {
    if (onUpdate) {
      onUpdate(task.id, updates);
    }
  };

  const handleAddAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    if (!assignees.includes(assignee)) {
      handleUpdate({ assignees: [...assignees, assignee] });
    }
  };

  const handleRemoveAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    handleUpdate({ assignees: assignees.filter((a) => a !== assignee) });
  };

  const handleAddTag = (tag: string) => {
    const tags = task.tags || [];
    if (!tags.includes(tag)) {
      handleUpdate({ tags: [...tags, tag] });
    }
  };

  const handleRemoveTag = (tag: string) => {
    const tags = task.tags || [];
    handleUpdate({ tags: tags.filter((t) => t !== tag) });
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      className={clsx(
        'p-3 rounded-xl border cursor-grab transition-all group',
        isDragging && 'opacity-50 scale-95',
        isSelected
          ? 'bg-accent-violet/10 border-accent-violet/30'
          : isOverdue
            ? 'bg-accent-rose/5 border-accent-rose/20 hover:border-accent-rose/40'
            : 'bg-surface-primary border-border-primary hover:border-border-secondary hover:shadow-lg'
      )}
      style={{ marginBottom: 8 }}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        <div className="mt-0.5 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Status - Inline Editable */}
        <div className="mt-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <StatusSelect
            value={task.status}
            onChange={(status) => handleUpdate({ status })}
            size="sm"
            showLabel={false}
            disabled={!canEdit}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Task Name - Clickable to open detail */}
          <h3
            className="text-[13px] font-medium text-text-primary line-clamp-2 leading-snug cursor-pointer hover:text-accent-violet transition-colors"
            onClick={onClick}
          >
            {task.name}
          </h3>

          {/* Meta Row with inline editors */}
          <div className="flex items-center flex-wrap gap-2 mt-2">
            {/* Location */}
            <span className="text-[10px] text-text-quaternary truncate max-w-[150px]">
              {task.spaceName} / {task.listName}
            </span>

            {/* Priority - Inline Editable */}
            <div onClick={(e) => e.stopPropagation()}>
              <PrioritySelect
                value={task.priority ?? null}
                onChange={(priority) => handleUpdate({ priority })}
                size="sm"
                showLabel={false}
                disabled={!canEdit}
              />
            </div>

            {/* Due Date - Inline Editable */}
            <div onClick={(e) => e.stopPropagation()}>
              <InlineDatePicker
                value={task.dueDate}
                onChange={(dueDate) => handleUpdate({ dueDate })}
                size="sm"
                showTime={false}
                disabled={!canEdit}
                highlight={isOverdue}
              />
            </div>
          </div>

          {/* Tags - Inline Editable */}
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <TagsEditor
              tags={task.tags || []}
              availableTags={availableTags}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
              size="sm"
              maxDisplay={2}
              disabled={!canEdit}
            />
          </div>

          {/* Custom Fields - Inline Display */}
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <CustomFieldsInline
              task={task}
              onUpdate={canEdit ? handleUpdate : undefined}
              maxDisplay={2}
              size="sm"
            />
          </div>
        </div>

        {/* Assignees - Inline Editable */}
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <AssigneesEditor
            assignees={task.assignees || []}
            availableAssignees={availableAssignees}
            onAdd={handleAddAssignee}
            onRemove={handleRemoveAssignee}
            size="sm"
            maxDisplay={2}
            disabled={!canEdit}
          />
        </div>
      </div>
    </div>
  );
}

interface CalendarTaskPillProps {
  task: Task;
  onClick: () => void;
  isSelected: boolean;
  isOverdue: boolean;
}

// ============================================
// TODAY PANEL (My Day inline in Inbox)
// ============================================

interface TodayPanelProps {
  myDay: ReturnType<typeof useMyDay>;
  onTaskClick: (task: Task) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask: (data: Partial<Task>) => Promise<void>;
}

// Format milliseconds to human readable time
function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '0m';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

// Format time from timestamp
function _formatTime(timestamp: number): string {
  return format(new Date(timestamp), 'HH:mm');
}

// Priority colors for left border
const priorityBorderColors: Record<string, string> = {
  urgent: 'border-l-accent-rose',
  high: 'border-l-accent-amber',
  normal: 'border-l-accent-blue',
  low: 'border-l-accent-emerald',
};

// Section icons and colors
const sectionConfig: Record<string, { icon: typeof Target; color: string; bgColor: string }> = {
  focus: { icon: Target, color: 'text-accent-violet', bgColor: 'bg-accent-violet/10' },
  'due-today': { icon: Clock, color: 'text-accent-amber', bgColor: 'bg-accent-amber/10' },
  overdue: { icon: AlertTriangle, color: 'text-accent-rose', bgColor: 'bg-accent-rose/10' },
  upcoming: { icon: CalendarDays, color: 'text-accent-blue', bgColor: 'bg-accent-blue/10' },
};

function TodayPanel({ myDay, onTaskClick, onTaskUpdate, onCreateTask }: TodayPanelProps) {
  const { greeting, dateLabel, userName, sections, totalScheduledToday, totalTimeEstimate } = myDay;
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [quickAddValue, setQuickAddValue] = useState('');
  const [isQuickAddFocused, setIsQuickAddFocused] = useState(false);

  const firstName = userName.split(' ')[0] || userName;

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const handleQuickAdd = useCallback(async () => {
    const name = quickAddValue.trim();
    if (!name) return;
    try {
      await onCreateTask({ name, dueDate: new Date().setHours(0, 0, 0, 0) });
      setQuickAddValue('');
    } catch { /* handled upstream */ }
  }, [quickAddValue, onCreateTask]);

  const handleQuickAddKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); handleQuickAdd(); }
    if (e.key === 'Escape') {
      setQuickAddValue('');
      setIsQuickAddFocused(false);
      (e.target as HTMLInputElement).blur();
    }
  }, [handleQuickAdd]);

  // Group upcoming tasks by day
  const upcomingByDay = useMemo(() => {
    const dayMap = new Map<string, Array<Task & { id: string }>>();
    for (const task of sections.upcoming.tasks) {
      if (!task.dueDate) continue;
      const dayKey = format(new Date(task.dueDate), 'yyyy-MM-dd');
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
      dayMap.get(dayKey)!.push(task);
    }
    const groups: { date: Date; label: string; tasks: Array<Task & { id: string }> }[] = [];
    for (const [dayKey, tasks] of dayMap) {
      const date = new Date(dayKey);
      groups.push({ date, label: format(date, "EEEE d MMMM", { locale: it }), tasks });
    }
    groups.sort((a, b) => a.date.getTime() - b.date.getTime());
    return groups;
  }, [sections.upcoming.tasks]);

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header with greeting */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-text-primary">
              {greeting}, {firstName}
            </h1>
            <p className="text-[13px] text-text-tertiary capitalize mt-0.5">{dateLabel}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-secondary/50 border border-border-primary">
              <ListChecks className="w-3.5 h-3.5 text-accent-violet" />
              <span className="text-[11px] font-medium text-text-secondary">{totalScheduledToday} task</span>
            </div>
            {totalTimeEstimate > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-secondary/50 border border-border-primary">
                <Timer className="w-3.5 h-3.5 text-accent-emerald" />
                <span className="text-[11px] font-medium text-text-secondary">{formatDuration(totalTimeEstimate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Quick Add */}
        <div
          className={clsx(
            'flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all',
            isQuickAddFocused
              ? 'border-accent-violet bg-surface-primary shadow-sm'
              : 'border-border-primary bg-surface-secondary/30 hover:border-border-secondary'
          )}
        >
          <Plus className={clsx('w-4 h-4 shrink-0', isQuickAddFocused ? 'text-accent-violet' : 'text-text-quaternary')} />
          <input
            type="text"
            value={quickAddValue}
            onChange={(e) => setQuickAddValue(e.target.value)}
            onFocus={() => setIsQuickAddFocused(true)}
            onBlur={() => setIsQuickAddFocused(false)}
            onKeyDown={handleQuickAddKeyDown}
            placeholder="Aggiungi un task per oggi..."
            className="flex-1 bg-transparent text-[13px] text-text-primary placeholder-text-quaternary focus:outline-none"
          />
          {quickAddValue.trim() && (
            <button
              onClick={handleQuickAdd}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-white bg-accent-violet hover:bg-accent-violet-light transition-colors"
            >
              Aggiungi
            </button>
          )}
        </div>

        {/* Sections */}
        <InboxMyDaySection
          section={sections.focus}
          isCollapsed={collapsedSections.has('focus')}
          onToggle={() => toggleSection('focus')}
          onTaskClick={onTaskClick}
          onUpdateTask={onTaskUpdate}
        />

        {sections.dueToday.tasks.length > 0 && (
          <InboxMyDaySection
            section={sections.dueToday}
            isCollapsed={collapsedSections.has('due-today')}
            onToggle={() => toggleSection('due-today')}
            onTaskClick={onTaskClick}
            onUpdateTask={onTaskUpdate}
          />
        )}

        {sections.overdue.tasks.length > 0 && (
          <InboxMyDaySection
            section={sections.overdue}
            isCollapsed={collapsedSections.has('overdue')}
            onToggle={() => toggleSection('overdue')}
            onTaskClick={onTaskClick}
            onUpdateTask={onTaskUpdate}
          />
        )}

        {/* Upcoming Section with day grouping */}
        {sections.upcoming.tasks.length > 0 && (
          <div className="bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
            <button
              onClick={() => toggleSection('upcoming')}
              className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-surface-hover/50 transition-colors"
            >
              {collapsedSections.has('upcoming') ? (
                <ChevronRight className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
              )}
              <div className={clsx('w-6 h-6 rounded-md flex items-center justify-center shrink-0', sectionConfig.upcoming.bgColor)}>
                <CalendarDays className={clsx('w-3.5 h-3.5', sectionConfig.upcoming.color)} />
              </div>
              <span className="text-[13px] font-semibold text-text-primary">{sections.upcoming.label}</span>
              <span className="text-[11px] text-text-quaternary font-medium">{sections.upcoming.tasks.length}</span>
            </button>
            {!collapsedSections.has('upcoming') && (
              <div className="px-4 pb-3 space-y-3">
                {upcomingByDay.map((group) => (
                  <div key={group.label}>
                    <div className="text-[11px] font-medium text-text-tertiary capitalize mb-1.5 px-1">{group.label}</div>
                    <div className="space-y-1.5">
                      {group.tasks.map((task) => (
                        <InboxMyDayTaskCard key={task.id} task={task} onTaskClick={onTaskClick} onUpdateTask={onTaskUpdate} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {totalScheduledToday === 0 && sections.overdue.tasks.length === 0 && sections.upcoming.tasks.length === 0 && (
          <div className="text-center py-12">
            <div className="w-14 h-14 rounded-2xl bg-accent-amber/10 flex items-center justify-center mx-auto mb-4">
              <Sun className="w-7 h-7 text-accent-amber" />
            </div>
            <h3 className="text-sm font-semibold text-text-primary mb-1">Nessun task per oggi</h3>
            <p className="text-[12px] text-text-tertiary max-w-xs mx-auto">
              Usa il campo qui sopra per aggiungere un task o pianifica i tuoi task dal calendario.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Inline MyDay Section for Inbox
interface InboxMyDaySectionProps {
  section: MyDaySection;
  isCollapsed: boolean;
  onToggle: () => void;
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
}

function InboxMyDaySection({ section, isCollapsed, onToggle, onTaskClick, onUpdateTask }: InboxMyDaySectionProps) {
  const config = sectionConfig[section.id] || sectionConfig.focus;
  const SectionIcon = config.icon;

  return (
    <div className="bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-surface-hover/50 transition-colors"
      >
        {isCollapsed ? (
          <ChevronRight className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
        )}
        <div className={clsx('w-6 h-6 rounded-md flex items-center justify-center shrink-0', config.bgColor)}>
          <SectionIcon className={clsx('w-3.5 h-3.5', config.color)} />
        </div>
        <span className="text-[13px] font-semibold text-text-primary">{section.label}</span>
        <span className="text-[11px] text-text-quaternary font-medium">{section.tasks.length}</span>
        {section.id === 'overdue' && section.tasks.length > 0 && (
          <span className="ml-auto text-[10px] font-medium text-accent-rose bg-accent-rose/10 px-2 py-0.5 rounded-full">Attenzione</span>
        )}
      </button>
      {!isCollapsed && (
        <div className="px-4 pb-3 space-y-1.5">
          {section.tasks.length === 0 ? (
            <div className="text-[11px] text-text-quaternary text-center py-4">Nessun task in questa sezione</div>
          ) : (
            section.tasks.map((task) => (
              <InboxMyDayTaskCard
                key={task.id}
                task={task}
                onTaskClick={onTaskClick}
                onUpdateTask={onUpdateTask}
                isFocusSection={section.id === 'focus'}
                showOverdueWarning={section.id === 'overdue'}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Inline MyDay Task Card for Inbox
interface InboxMyDayTaskCardProps {
  task: Task & { id: string };
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  isFocusSection?: boolean;
  showOverdueWarning?: boolean;
}

function InboxMyDayTaskCard({ task, onTaskClick, onUpdateTask, isFocusSection = false, showOverdueWarning = false }: InboxMyDayTaskCardProps) {
  const completed = isCompletedStatus(task.status);

  const handleStatusChange = useCallback((status: string) => {
    onUpdateTask?.(task.id, { status });
  }, [task.id, onUpdateTask]);

  const timeLabel = useMemo(() => {
    const dateVal = task.startDate ?? task.dueDate;
    if (dateVal) return format(new Date(dateVal), 'd MMM', { locale: it });
    return null;
  }, [task.startDate, task.dueDate]);

  const [_nowMs] = useState(() => Date.now());
  const daysOverdue = useMemo(() => {
    if (!showOverdueWarning || !task.dueDate) return 0;
    return Math.floor((_nowMs - task.dueDate) / 86_400_000);
  }, [task.dueDate, showOverdueWarning, _nowMs]);

  return (
    <div
      className={clsx(
        'flex items-center gap-2.5 px-3 py-2 rounded-lg border-l-2 transition-all cursor-pointer group',
        completed ? 'bg-surface-tertiary/30 border-l-border-primary' : 'bg-surface-primary hover:shadow-sm',
        !completed && task.priority ? priorityBorderColors[task.priority] || 'border-l-border-primary' : !completed ? 'border-l-border-primary' : '',
        showOverdueWarning && !completed && 'border-l-accent-rose'
      )}
      onClick={() => onTaskClick?.(task)}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <StatusSelect value={task.status} onChange={handleStatusChange} size="sm" showLabel={false} />
      </div>
      <span className={clsx('flex-1 text-[12px] font-medium truncate', completed ? 'text-text-tertiary line-through' : 'text-text-primary')}>
        {task.name}
      </span>
      {timeLabel && (
        <div className={clsx('flex items-center gap-1 shrink-0', isFocusSection ? 'text-accent-violet' : showOverdueWarning ? 'text-accent-rose' : 'text-text-quaternary')}>
          <Clock className="w-3 h-3" />
          <span className="text-[10px] font-medium">{timeLabel}</span>
        </div>
      )}
      {showOverdueWarning && daysOverdue > 0 && !completed && (
        <span className="text-[10px] font-semibold text-accent-rose bg-accent-rose/10 px-1.5 py-0.5 rounded-full shrink-0">{daysOverdue}g</span>
      )}
      {task.timeEstimated && task.timeEstimated > 0 && (
        <span className="text-[10px] text-text-quaternary shrink-0">{formatDuration(task.timeEstimated)}</span>
      )}
    </div>
  );
}

// ============================================
// INBOX MONTH GRID (simple monthly calendar)
// ============================================

interface InboxMonthGridProps {
  calendarHook: ReturnType<typeof useCalendar>;
  visibleTasks: Task[];
  tasksByDate: Map<string, Task[]>;
  selectedTaskId: string | null;
  today: Date;
  draggedTask: Task | null;
  dropTargetDate: Date | null;
  onDragOver: (e: DragEvent<HTMLDivElement>, date: Date) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>, date: Date) => void;
  onTaskClick: (task: Task) => void;
}

const weekDayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

function InboxMonthGrid({
  calendarHook,
  tasksByDate,
  selectedTaskId,
  today,
  draggedTask,
  dropTargetDate,
  onDragOver,
  onDragLeave,
  onDrop,
  onTaskClick,
}: InboxMonthGridProps) {
  return (
    <div className="flex-1 overflow-auto p-4">
      {/* Week days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDayNames.map((day) => (
          <div key={day} className="h-8 flex items-center justify-center text-[10px] font-semibold text-text-quaternary uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>
      {/* Calendar days grid */}
      <div className="grid grid-cols-7 gap-1 auto-rows-[minmax(100px,1fr)]">
        {calendarHook.calendarDays.map((calDay) => {
          const dateKey = format(calDay.date, 'yyyy-MM-dd');
          const dayTasks = tasksByDate.get(dateKey) || [];
          const isTodayDate = isToday(calDay.date);
          const isDropTarget = dropTargetDate && isSameDay(calDay.date, dropTargetDate);
          const hasOverdueTasks = dayTasks.some(() => {
            const d = new Date(calDay.date);
            d.setHours(0, 0, 0, 0);
            return d < today;
          });

          return (
            <div
              key={dateKey}
              className={clsx(
                'rounded-lg border p-2 transition-all overflow-hidden group',
                calDay.isCurrentMonth ? 'bg-surface-secondary border-border-primary' : 'bg-surface-tertiary/30 border-transparent',
                isTodayDate && 'ring-2 ring-accent-violet ring-offset-1 ring-offset-surface-primary',
                isDropTarget && 'bg-accent-violet/20 border-accent-violet/50 scale-[1.02]',
                draggedTask && 'cursor-copy'
              )}
              onDragOver={(e) => onDragOver(e, calDay.date)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, calDay.date)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={clsx(
                  'text-[11px] font-semibold w-6 h-6 rounded-full flex items-center justify-center',
                  isTodayDate ? 'bg-accent-violet text-white' : calDay.isCurrentMonth ? 'text-text-primary' : 'text-text-quaternary'
                )}>
                  {format(calDay.date, 'd')}
                </span>
                {dayTasks.length > 0 && (
                  <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded-full', hasOverdueTasks ? 'bg-accent-rose/20 text-accent-rose' : 'bg-accent-blue/20 text-accent-blue')}>
                    {dayTasks.length}
                  </span>
                )}
              </div>
              <div className="space-y-1 max-h-[80px] overflow-y-auto scrollbar-thin">
                {dayTasks.slice(0, 3).map((task) => (
                  <CalendarTaskPill
                    key={task.id}
                    task={task}
                    onClick={() => onTaskClick(task)}
                    isSelected={selectedTaskId === task.id}
                    isOverdue={calDay.date < today}
                  />
                ))}
                {dayTasks.length > 3 && (
                  <div className="text-[9px] text-text-quaternary font-medium px-1.5 py-0.5">
                    +{dayTasks.length - 3} altri
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarTaskPill({ task, onClick, isSelected, isOverdue }: CalendarTaskPillProps) {
  const status = normalizeStatus(task.status);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={clsx(
        'w-full text-left px-1.5 py-1 rounded text-[10px] font-medium truncate transition-all',
        isSelected
          ? 'bg-accent-violet text-white'
          : isOverdue
            ? 'bg-accent-rose/20 text-accent-rose hover:bg-accent-rose/30'
            : status === TASK_STATUS.IN_PROGRESS
              ? 'bg-accent-blue/20 text-accent-blue hover:bg-accent-blue/30'
              : status === TASK_STATUS.BLOCKED
                ? 'bg-accent-rose/20 text-accent-rose hover:bg-accent-rose/30'
                : 'bg-surface-tertiary text-text-secondary hover:bg-surface-hover'
      )}
      title={task.name}
    >
      {task.name}
    </button>
  );
}
